import { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Upload,
  FileSpreadsheet,
  FileText,
  Download,
  ChevronRight,
  ChevronLeft,
  Check,
  X,
  AlertCircle,
  Trash2,
} from "lucide-react";
import { OnboardingProgress } from "@/components/onboarding/OnboardingProgress";
import {
  parseHoldingsCSV,
  parseHoldingsExcel,
  downloadHoldingsCSV,
  downloadHoldingsExcel,
  type HoldingsRow,
  type ParseResult,
} from "@/lib/import-templates";

interface Family {
  id: string;
  name: string;
  org_id: string;
  onboarding_status: string | null;
}

type ImportMethod = "template" | "document" | "manual";

export default function PortfolioImport() {
  const { familyId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { orgId } = useTenant();

  const [step, setStep] = useState(1);
  const [importMethod, setImportMethod] = useState<ImportMethod | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult<HoldingsRow> | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [editingData, setEditingData] = useState<HoldingsRow[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch family details
  const { data: family, isLoading: familyLoading } = useQuery({
    queryKey: ["family-for-import", familyId],
    queryFn: async () => {
      if (!familyId) return null;
      const { data, error } = await supabase
        .from("families")
        .select("id, name, org_id, onboarding_status")
        .eq("id", familyId)
        .single();
      if (error) throw error;
      return data as Family;
    },
    enabled: !!familyId,
  });

  // Import holdings mutation
  const importMutation = useMutation({
    mutationFn: async (holdings: HoldingsRow[]) => {
      if (!familyId || !orgId) throw new Error("Missing family or org ID");

      // Get or create entity for the family (default entity)
      let entityId: string;
      const { data: existingEntities } = await supabase
        .from("entities")
        .select("id")
        .eq("family_id", familyId)
        .limit(1);

      if (existingEntities && existingEntities.length > 0) {
        entityId = existingEntities[0].id;
      } else {
        // Create a default entity
        const { data: newEntity, error: entityError } = await supabase
          .from("entities")
          .insert({
            org_id: orgId,
            family_id: familyId,
            name: `${family?.name || "Family"} - Main`,
            entity_type: "individual",
          })
          .select()
          .single();

        if (entityError) throw entityError;
        entityId = newEntity.id;
      }

      // Insert holdings
      const holdingsToInsert = holdings.map((h) => ({
        org_id: orgId,
        family_id: familyId,
        entity_id: entityId,
        fund_name: h.fund_name,
        fund_type: h.fund_type || "private_fund",
        commitment: h.commitment,
        paid_in: h.paid_in || 0,
        nav: h.nav || 0,
        distributions: h.distributions || 0,
        as_of: h.as_of_date,
        currency: h.currency,
        meta: h.notes ? { notes: h.notes } : {},
      }));

      const { error: holdingsError } = await supabase
        .from("holdings")
        .insert(holdingsToInsert);

      if (holdingsError) throw holdingsError;

      // Update family status to review
      const { error: statusError } = await supabase
        .from("families")
        .update({ onboarding_status: "review" })
        .eq("id", familyId);

      if (statusError) throw statusError;

      return holdings.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["holdings"] });
      queryClient.invalidateQueries({ queryKey: ["families"] });
      toast({
        title: "Import Complete",
        description: `Successfully imported ${count} holdings.`,
      });
      navigate(`/firm/families/${familyId}`);
    },
    onError: (error) => {
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to import holdings",
        variant: "destructive",
      });
    },
  });

  // Handle file upload
  const handleFileUpload = useCallback(async (file: File) => {
    setIsUploading(true);
    try {
      let result: ParseResult<HoldingsRow>;

      if (file.name.endsWith(".csv")) {
        const content = await file.text();
        result = parseHoldingsCSV(content);
      } else if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
        result = await parseHoldingsExcel(file);
      } else {
        throw new Error("Unsupported file format. Please use CSV or Excel.");
      }

      setParseResult(result);
      setEditingData([...result.data]);
      setSelectedRows(new Set(result.data.map((_, i) => i)));

      if (result.errors.length > 0) {
        toast({
          title: "Parsing Complete with Warnings",
          description: `${result.data.length} rows parsed, ${result.errors.length} errors found.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "File Parsed Successfully",
          description: `${result.data.length} holdings found.`,
        });
      }

      setStep(3);
    } catch (error) {
      toast({
        title: "Parse Error",
        description: error instanceof Error ? error.message : "Failed to parse file",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  }, [toast]);

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Handle row selection
  const toggleRowSelection = (index: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === editingData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(editingData.map((_, i) => i)));
    }
  };

  // Handle cell edit
  const handleCellEdit = (index: number, field: keyof HoldingsRow, value: string | number) => {
    const newData = [...editingData];
    newData[index] = { ...newData[index], [field]: value };
    setEditingData(newData);
  };

  // Handle row delete
  const handleDeleteRow = (index: number) => {
    const newData = editingData.filter((_, i) => i !== index);
    setEditingData(newData);
    const newSelected = new Set<number>();
    selectedRows.forEach((i) => {
      if (i < index) newSelected.add(i);
      else if (i > index) newSelected.add(i - 1);
    });
    setSelectedRows(newSelected);
  };

  // Handle import
  const handleImport = () => {
    const rowsToImport = editingData.filter((_, i) => selectedRows.has(i));
    if (rowsToImport.length === 0) {
      toast({
        title: "No Rows Selected",
        description: "Please select at least one row to import.",
        variant: "destructive",
      });
      return;
    }
    importMutation.mutate(rowsToImport);
  };

  if (familyLoading || !family) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container max-w-5xl py-8">
      <OnboardingProgress status={family.onboarding_status} className="mb-8" />

      <div className="mb-6">
        <h1 className="text-2xl font-semibold mb-2">Portfolio Import</h1>
        <p className="text-muted-foreground">
          Import portfolio data for <strong>{family.name}</strong>
        </p>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[
          { num: 1, label: "Choose Method" },
          { num: 2, label: "Upload File" },
          { num: 3, label: "Review Data" },
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center gap-2 flex-1">
            <div
              className={`flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium ${
                s.num <= step
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {s.num < step ? <Check className="h-4 w-4" /> : s.num}
            </div>
            <span className={`text-sm ${s.num <= step ? "font-medium" : "text-muted-foreground"}`}>
              {s.label}
            </span>
            {idx < 2 && <div className={`flex-1 h-1 rounded-full ${s.num < step ? "bg-primary/40" : "bg-muted"}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Choose Method */}
      {step === 1 && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card
            className={`cursor-pointer transition-all hover:border-primary ${
              importMethod === "template" ? "border-primary ring-2 ring-primary/20" : ""
            }`}
            onClick={() => setImportMethod("template")}
          >
            <CardHeader>
              <FileSpreadsheet className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">Template Import</CardTitle>
              <CardDescription>
                Download a template, fill in your data, and upload
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• CSV or Excel format</li>
                <li>• Pre-defined columns</li>
                <li>• Best for structured data</li>
              </ul>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:border-primary ${
              importMethod === "document" ? "border-primary ring-2 ring-primary/20" : ""
            }`}
            onClick={() => setImportMethod("document")}
          >
            <CardHeader>
              <FileText className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">Document Upload</CardTitle>
              <CardDescription>
                Upload statements and extract data automatically
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• PDF statements</li>
                <li>• AI-powered extraction</li>
                <li>• Review before import</li>
              </ul>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-all hover:border-primary ${
              importMethod === "manual" ? "border-primary ring-2 ring-primary/20" : ""
            }`}
            onClick={() => setImportMethod("manual")}
          >
            <CardHeader>
              <Upload className="h-8 w-8 text-primary mb-2" />
              <CardTitle className="text-lg">Manual Entry</CardTitle>
              <CardDescription>
                Enter holdings one by one using the data entry form
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Form-based entry</li>
                <li>• Good for few holdings</li>
                <li>• Full control</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 2: Upload File (Template method) */}
      {step === 2 && importMethod === "template" && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Your Data</CardTitle>
            <CardDescription>
              Upload a CSV or Excel file with your holdings data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Download templates */}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={downloadHoldingsCSV}>
                <Download className="h-4 w-4" />
                Download CSV Template
              </Button>
              <Button variant="outline" size="sm" onClick={downloadHoldingsExcel}>
                <Download className="h-4 w-4" />
                Download Excel Template
              </Button>
            </div>

            {/* Upload zone */}
            <div
              className="border-2 border-dashed rounded-lg p-12 text-center hover:border-primary transition-colors"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              {isUploading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mb-4" />
                  <p className="text-sm text-muted-foreground">Parsing file...</p>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">
                    Drag and drop your file here, or click to browse
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Supports CSV and Excel (.xlsx, .xls)
                  </p>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    id="file-upload"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                  <Button asChild variant="outline">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      Choose File
                    </label>
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Document Upload */}
      {step === 2 && importMethod === "document" && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Documents</CardTitle>
            <CardDescription>
              Upload PDF statements for automatic data extraction
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                Document extraction is available in the Data section.
              </p>
              <Button onClick={() => navigate("/data/ingestion")}>
                Go to Document Upload
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Manual Entry */}
      {step === 2 && importMethod === "manual" && (
        <Card>
          <CardHeader>
            <CardTitle>Manual Entry</CardTitle>
            <CardDescription>
              Enter holdings one by one
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                Manual data entry is available in the Portfolio section.
              </p>
              <Button onClick={() => navigate("/portfolio/positions")}>
                Go to Portfolio
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Review Data */}
      {step === 3 && parseResult && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Review Import Data</CardTitle>
                <CardDescription>
                  {editingData.length} holdings parsed • {selectedRows.size} selected for import
                </CardDescription>
              </div>
              {parseResult.errors.length > 0 && (
                <Badge variant="destructive">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {parseResult.errors.length} warnings
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Errors display */}
            {parseResult.errors.length > 0 && (
              <div className="mb-4 p-3 bg-destructive/10 rounded-lg">
                <p className="text-sm font-medium text-destructive mb-2">Parsing Warnings:</p>
                <ul className="text-xs text-destructive space-y-1 max-h-24 overflow-y-auto">
                  {parseResult.errors.slice(0, 10).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                  {parseResult.errors.length > 10 && (
                    <li>... and {parseResult.errors.length - 10} more</li>
                  )}
                </ul>
              </div>
            )}

            {/* Data table */}
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selectedRows.size === editingData.length}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Fund Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Commitment</TableHead>
                    <TableHead className="text-right">Paid In</TableHead>
                    <TableHead className="text-right">NAV</TableHead>
                    <TableHead>As Of</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editingData.map((row, idx) => (
                    <TableRow key={idx} className={selectedRows.has(idx) ? "" : "opacity-50"}>
                      <TableCell>
                        <Checkbox
                          checked={selectedRows.has(idx)}
                          onCheckedChange={() => toggleRowSelection(idx)}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.fund_name}
                          onChange={(e) => handleCellEdit(idx, "fund_name", e.target.value)}
                          className="h-8 text-sm"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={row.fund_type || "private_fund"}
                          onValueChange={(v) => handleCellEdit(idx, "fund_type", v)}
                        >
                          <SelectTrigger className="h-8 text-sm w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="private_fund">Private Fund</SelectItem>
                            <SelectItem value="public_fund">Public Fund</SelectItem>
                            <SelectItem value="direct">Direct</SelectItem>
                            <SelectItem value="co_invest">Co-Invest</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          value={row.commitment}
                          onChange={(e) => handleCellEdit(idx, "commitment", parseFloat(e.target.value) || 0)}
                          className="h-8 text-sm text-right w-28"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          value={row.paid_in || ""}
                          onChange={(e) => handleCellEdit(idx, "paid_in", parseFloat(e.target.value) || 0)}
                          className="h-8 text-sm text-right w-28"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          value={row.nav || ""}
                          onChange={(e) => handleCellEdit(idx, "nav", parseFloat(e.target.value) || 0)}
                          className="h-8 text-sm text-right w-28"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="date"
                          value={row.as_of_date}
                          onChange={(e) => handleCellEdit(idx, "as_of_date", e.target.value)}
                          className="h-8 text-sm w-32"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDeleteRow(idx)}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        {step > 1 ? (
          <Button variant="outline" onClick={() => setStep(step - 1)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        ) : (
          <Button variant="outline" onClick={() => navigate(`/firm/families/${familyId}`)}>
            Cancel
          </Button>
        )}

        {step === 1 && importMethod && (
          <Button onClick={() => setStep(2)}>
            Continue
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}

        {step === 3 && (
          <Button onClick={handleImport} disabled={importMutation.isPending || selectedRows.size === 0}>
            {importMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Import {selectedRows.size} Holdings
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
